import React, { useCallback, useMemo } from 'react'
import { useRouter } from 'next/router'
import { recipeUtils } from '@jowtech/utils'
import { useSelector } from 'react-redux'
import dynamic from 'next/dynamic'

import Meta from '../../components/Meta'
import BlockedContentPage from '../BlockedContentPage'
import UserGeneratedRecipe from '../../components/UserGeneratedRecipe'
import { useTranslation } from '../../contexts/I18nContext'
import {
  componentLoader,
  enhanceFormattedRecipeWithCoversCount,
} from '../../helpers'
import { recipePublishingEnabledSelector } from '../../store/selectors/config'
import PreventSleep from '../../components/PreventSleep'

import Styled from './UserGeneratedRecipePage.styled'

const RecipeTimerBubble = dynamic(
  () =>
    componentLoader(
      () =>
        import(
          /* webpackChunkName: "@components/RecipeTimerBubble" */ '../../components/RecipeTimerBubble'
        ),
    ),
  { ssr: false },
)

const UserGeneratedRecipePage = ({
  auth,
  config,
  createTimer,
  editMenu,
  favorRecipe,
  menu,
  openModal,
  profile,
  showError,
  timer,
  userGeneratedRecipe,
}) => {
  const { t } = useTranslation()
  const router = useRouter()

  const { coversCount: covers, replace: oldRecipeId, from } = router.query
  const coversCount = covers ? +covers : +recipeUtils.getCoversCount(profile)

  const formattedRecipe = useMemo(() => {
    return enhanceFormattedRecipeWithCoversCount(userGeneratedRecipe, {
      coversCount: +coversCount,
      coversOptions: userGeneratedRecipe.coversOptions,
    })
  }, [userGeneratedRecipe, coversCount])

  const enableRecipePublishing = useSelector(({ config }) =>
    recipePublishingEnabledSelector({ config }),
  )

  const blockedType = useMemo(() => {
    if (profile?.blockedEntities) {
      if (
        profile?.blockedEntities?.user?.includes(formattedRecipe?.author?.id)
      ) {
        return 'user'
      } else if (
        profile?.blockedEntities?.recipe?.some(
          ({ id, source }) =>
            id === formattedRecipe?.id && source === formattedRecipe?.source,
        )
      ) {
        return 'recipe'
      }
    }
    return ''
  }, [formattedRecipe, profile])

  const handleTimerClicked = useCallback(() => {
    openModal('Timer', {
      createTimer,
      recipe: {
        source: formattedRecipe.source,
        id: formattedRecipe.id,
        title: formattedRecipe.title,
        slug: formattedRecipe.slug,
        coversCount,
      },
    })
  }, [coversCount, openModal, formattedRecipe, createTimer])

  return (
    <PreventSleep floatingButtonDisplayScrollTop={256}>
      <Meta
        title={formattedRecipe?.title}
        description={t('META.RECIPE.DESCRIPTION', {
          title: formattedRecipe?.title,
          description: formattedRecipe.description,
          stepsCount: (formattedRecipe.directions || []).length,
          ingredients: (formattedRecipe.constituents || []).map(
            (ingredient) => ingredient.name,
          ),
          cookingTime:
            formattedRecipe.preparationTime + formattedRecipe.cookingTime,
          author: '',
        })}
        imageUrl={formattedRecipe.thumbnailUrl || formattedRecipe.imageUrl}
      >
        {formattedRecipe?.indexingStatus === 'index' ? null : (
          <meta name="robots" content="noindex" />
        )}
      </Meta>
      <Styled>
        <BlockedContentPage blockedType={blockedType}>
          <UserGeneratedRecipe
            auth={auth}
            config={config}
            editMenu={editMenu}
            enableRecipePublishing={enableRecipePublishing}
            favorRecipe={favorRecipe}
            from={from}
            isLoading={false}
            menu={menu}
            oldRecipeId={oldRecipeId}
            openModal={openModal}
            profile={profile}
            showError={showError}
            userGeneratedRecipe={formattedRecipe}
            userGeneratedRecipePayload={userGeneratedRecipe}
          />
        </BlockedContentPage>
      </Styled>

      {config?.reportRecipeTags?.length > 0 && <div id="report-recipe" />}

      {timer?.recipe ? null : (
        <RecipeTimerBubble onTimerClick={handleTimerClicked} />
      )}
    </PreventSleep>
  )
}

export default UserGeneratedRecipePage
